import { useCallback, useEffect } from "react";
import { LayoutComponent } from "../../../../../components/protected/layout-components/LayoutComponent";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash,
} from "react-bootstrap-icons";
import {
  AIChatIconColored,
  AlertIconFill,
  ArrowForwardIcon,
  CommentIcon,
  MoreIcon,
  ReplyAllIcon,
  ReplyIcon,
  SearchIcon,
  TrashIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import FilterIcon from "@spectrum-icons/workflow/Filter";
import {
  Flex,
  Content,
  ProgressCircle,
  Button,
  View,
  Link,
  ListBox,
  IllustratedMessage,
  Heading,
  Item,
  ActionButton,
  DialogTrigger,
  Dialog,
  TextField,
  DatePicker,
  Text,
  Checkbox,
  Divider,
  Form,
  Footer,
  Provider,
} from "@adobe/react-spectrum";
import { ToastQueue } from "@react-spectrum/toast";
import NotFound from "@spectrum-icons/illustrations/NotFound";
import moment from "moment";
import { useAsyncList } from "react-stately";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useViewInfo } from "../../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import CardComponent from "./MessagesCardComponent";
import MessageAttachment from "./MessagesAttachmentComponent";
import { useMessageCount } from "../../../../../contexts/MessageCountContext";
import NewMessageComponent from "./NewMessageComponent";
import MessagesListComponent from "./Messages_List_Component";
import { useMessagesState } from "../../../../../hooks/useMessagesState";
import { useMessageOperations } from "../../../../../hooks/useMessageOperations";
import { useMessageNodeId } from "../../../../../hooks/useMessageNodeId";
import ConceptCardComponent from "./ConceptCardComponent";
import {
  selectMessage,
  findMessageIndexById,
  getNextMessageAfterDeletion,
} from "../../utils/messageUtils";
import { invokeNewMessageCreating } from "../../../../../utils/NavigationUtils";
import { truncateText } from "../../../../../utils/helperUtil";
import "./messages_component.css";
import styles from "./messages_component.module.css";

/**
 * Helper function to extract recipient value from various data formats
 * Handles: arrays (of strings or objects), strings, and objects with CONTENT property
 */
const getRecipientValue = (data: any): string | null => {
  if (!data) return null;

  if (Array.isArray(data) && data.length > 0) {
    // Handle array of objects with CONTENT property or array of strings
    const values = data
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }
        if (item?.CONTENT && typeof item.CONTENT === "string") {
          return item.CONTENT.trim();
        }
        return null;
      })
      .filter(Boolean);

    return values.length > 0 ? values.join(", ") : null;
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data.CONTENT && typeof data.CONTENT === "string" && data.CONTENT.trim()) {
    return data.CONTENT.trim();
  }
  return null;
};

/**
 * Component to render the "To" and "Cc" recipient fields
 * Only renders fields that have valid data
 */
const RecipientToField = ({ recipient }: { recipient: any }) => {
  const toValue = getRecipientValue(recipient?.TO);
  const ccValue = getRecipientValue(recipient?.CC);
  console.log("ccValue", recipient?.CC);
  // Don't render anything if neither To nor Cc has data
  if (!toValue && !ccValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="size-200"
      UNSAFE_className={styles.email_to}
    >
      {toValue && (
        <View>
          <span title={toValue} style={{ color: "#728289" }}>
            To
          </span>
          &nbsp;{truncateText(toValue, 150)}
        </View>
      )}
      {ccValue && (
        <View>
          <span title={ccValue} style={{ color: "#728289" }}>
            Cc
          </span>
          &nbsp;{truncateText(ccValue, 150)}
        </View>
      )}
    </Flex>
  );
};

interface MessagesProps {
  setShowMobileMenu: (show: boolean) => void;
  messageType:
    | "todo"
    | "messages"
    | "sent"
    | "trash"
    | "corporate"
    | "concept"
    | "";
}
export interface Character {
  id: string;
  seen: string;
  sender?: string;
  subject?: string;
  date?: string;
  mailFrom?: string;
}

const MessagesList = ({ setShowMobileMenu, messageType }: MessagesProps) => {
  const { t } = useTranslation();
  const { store, messageService, mainService, accessService } = useDependency();
  const { isMobile } = useViewInfo();
  const { refreshMessageCounts } = useMessageCount();

  // Custom hooks for state management and operations
  const { state, refs, actions } = useMessagesState();

  // Initialize messageNodeId using custom hook
  useMessageNodeId({
    store,
    accessService,
    setMessageNodeId: actions.setMessageNodeId,
    dependencies: [messageType],
  });

  const {
    getMessageListCount,
    loadMessages,
    getEmailContent,
    deleteMessage: deleteMessageOp,
    utils,
    PAGE_SIZE,
  } = useMessageOperations({
    store,
    messageService,
    mainService,
    messageType,
    refs,
    messageNodeId: state.messageNodeId,
  });

  useEffect(() => {
    (async () => {
      actions.setRowsData([]);
      actions.setShowListName(messageType);
      resetMessageListState("");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageType]);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    actions.setTouchEnd(null); // Reset touch end
    actions.setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    actions.setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!state.touchStart || !state.touchEnd) return;

    const distance = state.touchStart - state.touchEnd;
    const isRightSwipe = distance < -minSwipeDistance;

    // Handle right swipe (swipe from left to right)
    if (isRightSwipe) {
      // Navigate back to list view
      setShowMobileMenu(true);
      actions.setShowDetail(false);
    }

    // You can also handle left swipe for other actions if needed
    // const isLeftSwipe = distance > minSwipeDistance;
    // if (isLeftSwipe) {
    //   // Handle left swipe
    // }
  };

  let listMessageList = useAsyncList<Character>({
    async load({ cursor, filterText }) {
      if (cursor && Number(cursor) * PAGE_SIZE >= state.totalMessageListRec) {
        actions.setShowLoader(false);
        actions.setShowLoaderDetail(false);
        return { items: state.rowsData, cursor: undefined };
      }

      try {
        let totalRecords = await getMessageListCount(filterText || "");
        actions.setNavigationIndex(0);
        totalRecords = totalRecords ? Number(totalRecords) : 0;
        let currentPage = cursor ? parseInt(cursor) : 0;
        let offSet = PAGE_SIZE * currentPage;
        refs.offSetLimitRef.current = offSet;
        let listMessages = await loadMessages(filterText || "");
        actions.appendRowsData(listMessages);
        actions.setTotalMessageListRec(totalRecords);
        if (currentPage === 0) {
          actions.setSelectedKeys(new Set([listMessages[0]?.id]));
          actions.setSelectedDataRow(listMessages[0]);

          //SET SELECTED ROW EMAIL CONTENT
          if (listMessages[0]) {
            let messageContent: any = await getEmailContent(listMessages[0]);
            actions.setMessageContent(messageContent);
          }
        }

        return {
          items: listMessages,
          cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
        };
      } catch (error) {
        console.error("Error loading messages:", error);
        // Turn off loader even on error
        actions.setShowLoader(false);
        actions.setShowLoaderDetail(false);
        return { items: [], cursor: undefined };
      } finally {
        actions.setShowLoader(false);
        actions.setShowLoaderDetail(false);
      }
    },
  });

  const resetMessageListState = (filterText: string) => {
    actions.resetMessageList();
    actions.setShowLoader(true); // Show loader when resetting
    refs.offSetLimitRef.current = 0;
    listMessageList.setFilterText(filterText);
  };

  const searchMessageList = useCallback(async () => {
    refs.filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState(refs.searchedTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadMessageList = useCallback(() => {
    refs.filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterMessageList = useCallback(() => {
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleListRowClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey;
    if (!currentKey || state.selectedKeys?.has(currentKey)) return;

    actions.setShowLoaderDetail(true);
    actions.setSelectedKeys(rowId);

    const recIndex = findMessageIndexById(state.rowsData, currentKey);
    actions.setNavigationIndex(recIndex);

    await selectMessage(currentKey, state.rowsData, actions, getEmailContent);
    actions.setShowLoaderDetail(false);
  };

  const handleListRowLargeClick = useCallback(
    async (rowId: any): Promise<void> => {
      const currentKey = rowId?.currentKey || state.selectedDataRow?.id;
      if (!currentKey) return;

      actions.setShowLoaderDetail(true);
      actions.setSelectedKeys(rowId ?? state.selectedKeys);
      const recIndex = state.rowsData.findIndex(
        (item: any) => item.id === currentKey,
      );
      actions.setNavigationIndex(recIndex);

      const selectedRow = state.rowsData.find(
        (item: any) => item.id === currentKey,
      );
      if (!selectedRow) {
        actions.setSelectedDataRow(null);
        actions.setSelectedKeys(new Set());
        actions.setShowLoaderDetail(false);
        return;
      }
      console.log("selectedRow", selectedRow);
      actions.setSelectedDataRow(selectedRow);

      const messageContent = await getEmailContent(selectedRow);
      actions.setMessageContent(messageContent);
      actions.setShowLoaderDetail(false);
      actions.setShowDetail(true);

      if (selectedRow.seen === "0") {
        listMessageList.update(currentKey, { ...selectedRow, seen: "1" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedKeys, state.selectedDataRow, state.rowsData],
  );

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex
      direction="row"
      alignContent="center"
      justifyContent="center"
      marginTop={"30px"}
    >
      <IllustratedMessage>
        <NotFound />
        <Heading>{t("no_results", { ns: "layout_components" })}</Heading>
      </IllustratedMessage>
    </Flex>
  );

  const nextNavigation = async () => {
    actions.setShowLoaderDetail(true);
    const nextMessageId = state.rowsData[state.navigationIndex + 1].id;

    const selectedRow = await selectMessage(
      nextMessageId,
      state.rowsData,
      actions,
      getEmailContent,
    );
    if (selectedRow) {
      actions.setNavigationIndex(state.navigationIndex + 1);
    }
    actions.setShowLoaderDetail(false);
  };

  const previousNavigation = async () => {
    if (state.navigationIndex <= 0) {
      return;
    }
    actions.setShowLoaderDetail(true);
    const prevMessageId = state.rowsData[state.navigationIndex - 1].id;

    const selectedRow = await selectMessage(
      prevMessageId,
      state.rowsData,
      actions,
      getEmailContent,
    );
    if (selectedRow) {
      actions.setNavigationIndex(state.navigationIndex - 1);
    }
    actions.setShowLoaderDetail(false);
  };

  // Reply and Reply All functionality using NewMessageComponent
  const handleReplyClick = async (replyType: "REPLY" | "REPLYALL") => {
    // Prepare the message object for reply
    const messageForReply = {
      ...state.selectedDataRow,
      MESSAGE: state.messageContent?.MESSAGE,
    };

    //console.log('messageForReply',messageForReply);

    // Set up the NewMessageComponent with reply data
    actions.setReplyType(replyType);
    actions.setReplyMessageObj(messageForReply);
    actions.setReplyEmailAttachments(
      state.messageContent?.processedAttachments || [],
    );
    actions.setShowNewMessageDialog(true);
  };

  const handleDeleteMessage = async (messageId: string) => {
    const success = await deleteMessageOp(messageId);

    if (success) {
      // Remove the message from the async list
      listMessageList.remove(messageId);

      // Get the next message in the list (if any) to show after deletion
      let nextMessageObj = getNextMessageAfterDeletion(
        state.rowsData,
        messageId,
      );

      // Update the rows data by filtering out the deleted message
      actions.setRowsData(
        state.rowsData.filter((item: any) => item.id !== messageId),
      );

      // Update the selected message to the next message in the list
      if (nextMessageObj) {
        await selectMessage(
          nextMessageObj.id,
          state.rowsData,
          actions,
          getEmailContent,
        );
      }

      // Show success notification
      ToastQueue.info(
        t("message_deleted_successfully", { ns: "layout_components" }),
        { timeout: 50 },
      );
    } else {
      // Show error notification if deletion failed
      ToastQueue.negative(
        t("error_deleting_message", { ns: "layout_components" }),
        { timeout: 50 },
      );
    }
  };

  const MessageDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_message", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("are_you_sure_you_want_to_remove_message", {
            ns: "layout_components",
          })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <div className={styles.trash_icon}>
              <TrashIcon size={32} />
            </div>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                handleDeleteMessage(state.selectedDataRow.id);
                close();
              }}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveMessageButton = () => (
    <DialogTrigger isDismissable type="modal">
      <ActionButton UNSAFE_className={styles.btn_delete}>
        <Trash className={styles.icon_trash} size={24} />
      </ActionButton>
      {(close) => (
        <MessageDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const SearchDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("search", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form validationBehavior="native">
          <TextField
            autoFocus
            isRequired
            label={t("search", { ns: "layout_components" })}
            maxWidth="100%"
            onChange={(e) => {
              refs.searchedTextRef.current = e;
            }}
            validationState="invalid"
          />
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_cancel}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_confirm}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                if (refs.searchedTextRef.current === "") {
                  return;
                }
                searchMessageList();
                refs.searchedTextRef.current = "";
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const SearchButton = () => (
    <DialogTrigger>
      <ActionButton UNSAFE_className={styles.no_border}>
        <SearchIcon size={24} />
      </ActionButton>
      {(close) => (
        <SearchDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const FilterDialog = ({ close }: { close: () => void }) => (
    <Dialog size="M" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("Filter", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form>
          <Provider locale="en-US" UNSAFE_style={{ width: "100%" }}>
            <DatePicker
              width={"100%"}
              autoFocus
              id="dtCreatedFrom"
              label="Created from"
              onChange={(e) =>
                (refs.filterParamRef.current.createdFrom = e?.toString() ?? "")
              }
            />
          </Provider>
          <Provider locale="en-US">
            <DatePicker
              width={"100%"}
              label="Created to"
              onChange={(e) =>
                (refs.filterParamRef.current.createdTo = e?.toString() ?? "")
              }
            />
          </Provider>
          <TextField
            label="Sender"
            onChange={(e) => (refs.filterParamRef.current.sender = e)}
          />
          <TextField
            label="Subject"
            onChange={(e) => (refs.filterParamRef.current.subject = e)}
          />
          <Checkbox onChange={(e) => (refs.filterParamRef.current.newOnly = e)}>
            Only new
          </Checkbox>
          <Checkbox onChange={(e) => (refs.filterParamRef.current.starred = e)}>
            Only starred
          </Checkbox>
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_cancel}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_confirm}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                filterMessageList();
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const FilterButton = () => (
    <DialogTrigger type="modal">
      <ActionButton UNSAFE_className={styles.no_border}>
        <FilterIcon UNSAFE_style={{ fill: "#b0bec5" }} />
      </ActionButton>
      {(close) => (
        <FilterDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  // TODO: Uncomment when menu action is needed
  // const menuAction = (key: string) => {
  //   if (key === "delete") {
  //     handleDeleteMessage(state.selectedDataRow.id);
  //   }
  // };

  const renderMessageList = () => {
    return state.showLoaderDetail ? (
      <View
        id="renderMessageList"
        position={"fixed"}
        width={"100%"}
        height={"400px"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <Loader />
      </View>
    ) : (
      <View
        position={"fixed"}
        width={"100%"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <View
          position={"absolute"}
          width={"100%"}
          height={"64px"}
          overflow={"hidden"}
        >
          <Flex
            direction={"row"}
            position={"absolute"}
            top={24}
            height={"32px"}
            alignItems={"center"}
            justifyContent={"center"}
            marginStart={12}
            UNSAFE_className={styles.btn_new_email}
            UNSAFE_style={{ display: "" }}
          >
            {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
            <Button
              id="btn_new_email_1"
              variant="primary"
              UNSAFE_style={{
                background: "transparent",
                border: "none",
                width: "100%",
                fontSize: "14px",
              }}
              onPress={() => {
                //setShowNewMessageDialog(true);
              }}
            >
              <>
                <Plus size={18} className={styles.btn_new_email_text} />
                <span className={styles.btn_new_email_text}>
                  {t("new_email", { ns: "layout_components" })}
                </span>
              </>
            </Button>
          </Flex>

          <Flex
            position={"absolute"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"end"}
            gap={18}
            right={10}
            top={25}
            UNSAFE_style={{ display: "none" }}
          >
            <ArrowClockwise
              className={styles.message_list_header_icon}
              onClick={reloadMessageList}
            />
            {/* <SearchIcon size={24} /> */}
            <SearchButton />
            {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
            <FilterButton />
            <MoreIcon size={24} />
          </Flex>
        </View>
        <View
          position={"absolute"}
          // top={'65px'}
          left={"0px"}
          width={"100%"}
          height={"100%"}
          overflow={"hidden"}
          UNSAFE_style={{
            fontSize: "12px",
            color: "#36474f",
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Flex
            direction={"column"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            width={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            maxHeight={"100vh"}
          >
            <MessagesListComponent
              listMessageList={listMessageList}
              rowsData={state.rowsData}
              handleListRowLargeClick={handleListRowLargeClick}
              totalMessageListRec={state.totalMessageListRec}
              selectedKeys={state.selectedKeys}
              isMobile={isMobile}
              isLoading={listMessageList.isLoading}
              items={listMessageList.items}
              selectedDataRow={state.selectedDataRow}
              messageType={messageType}
            />
          </Flex>
          <Flex
            direction={"row"}
            position={"fixed"}
            right={"16px"}
            bottom={"95px"}
            height={"48px"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={"8px"}
            UNSAFE_className={styles.add_new_parent}
          >
            <Link
              isQuiet
              onPress={(e) => {
                actions.setShowNewMessageDialog(true);
                //ToastQueue.info('Under Development', { timeout: 50 });
              }}
              UNSAFE_className={styles.add_new_btn}
            >
              <i className="bi bi-plus fs-5">
                <View UNSAFE_className={styles.add_new_btn_text}>
                  {t("New Email", { ns: "layout_components" })}
                </View>
              </i>
            </Link>
          </Flex>
        </View>
      </View>
    );
  };

  const renderMessageListDetailLarge = () => {
    return (
      <View
        width={"100%"}
        position={"fixed"}
        height={"100vh"}
        left={0}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"24px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        actions.setShowDetail(false);
                      }}
                    />
                  </View>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      UNSAFE_style={{
                        background: "transparent",
                        border: "none",
                      }}
                      onPress={() => handleReplyClick("REPLY")}
                      aria-label="Reply"
                    >
                      <ReplyIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      UNSAFE_style={{
                        background: "transparent",
                        border: "none",
                      }}
                      onPress={() => handleReplyClick("REPLYALL")}
                      aria-label="Reply All"
                    >
                      <ReplyAllIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      UNSAFE_style={{
                        background: "transparent",
                        border: "none",
                      }}
                      onPress={() =>
                        invokeNewMessageCreating(
                          state.selectedDataRow?.id,
                          "MessageDialog",
                          [],
                          [],
                        )
                      }
                      aria-label="Forward/New Message"
                    >
                      <ArrowForwardIcon size={24} />
                    </ActionButton>
                  </Flex>
                </Flex>
                <View
                  position={"relative"}
                  width={"1px"}
                  height={"24px"}
                  UNSAFE_className={styles.message_list_detail_header_divider}
                ></View>
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  width={"100px"}
                  UNSAFE_className={styles.btn_comment}
                >
                  <button
                    className={styles.btn_comment_text}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                  >
                    {t("comment", { ns: "layout_components" })}
                  </button>
                </Flex>
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"27px"}
                right={"20px"}
                alignItems={"center"}
                justifyContent={"start"}
                gap={"10px"}
              >
                <RemoveMessageButton />
                <MoreIcon size={24} />
              </Flex>
            </View>
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "5px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailLarge"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  {messageType !== "concept" && (
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      alignItems={"end"}
                      justifyContent={"space-between"}
                    >
                      <Flex
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        gap={"8px"}
                      >
                        <View UNSAFE_className={styles.user_full_name}>
                          {utils.getSenderName(state.selectedDataRow.sender)}
                        </View>
                        <View
                          width={"1px"}
                          position={"relative"}
                          height={"12px"}
                          UNSAFE_className={styles.user_detail_divider}
                        ></View>
                        <View UNSAFE_className={styles.user_email}>
                          {utils.getSenderEmail(state.selectedDataRow.mailFrom)}
                        </View>
                      </Flex>
                      <View
                        position={"absolute"}
                        right={"25px"}
                        UNSAFE_className={styles.div_date}
                      >
                        {moment(state.selectedDataRow.date).format(
                          "DD.MM.YYYY HH:mm",
                        )}
                      </View>
                    </Flex>
                  )}
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {state.selectedDataRow.subject}
                    </View>
                    <RecipientToField
                      recipient={state.selectedDataRow?.RECIPIENT}
                    />
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"6px"}
                    >
                      <View UNSAFE_className={styles.address}>
                        {state.messageContent.MESSAGE?.folderName}
                      </View>
                      {state.messageContent.MESSAGE?.projectName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {" "}
                            {">"}{" "}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {state.messageContent.MESSAGE?.projectName}
                          </View>
                        </>
                      )}
                      {state.messageContent.MESSAGE?.activityName && (
                        <>
                          <View UNSAFE_className={styles.icon_chevron_right}>
                            {" "}
                            {">"}{" "}
                          </View>
                          <View UNSAFE_className={styles.address}>
                            {state.messageContent.MESSAGE?.activityName}
                          </View>
                        </>
                      )}
                    </Flex>
                    {state.messageContent.participantsName &&
                      state.messageContent.type === "12" && (
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={"6px"}
                        >
                          <View UNSAFE_className={styles.address}>
                            Participants:&nbsp;
                            {state.messageContent.participantsName}
                          </View>
                        </Flex>
                      )}
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={state.messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"75%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: messageType != 'concept' ? utils.oldEmailContentFix(state.messageContent.MESSAGE?.PREVIEW?.content) : state.messageContent.messageTemplate?.BODY?.string,
                }}
              /> */}
              <iframe
                srcDoc={
                  messageType !== "concept"
                    ? utils.oldEmailContentFix(
                        state.messageContent.MESSAGE?.PREVIEW?.content,
                      )
                    : state.messageContent.messageTemplate?.BODY?.string
                }
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"16px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {state.navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {state.navigationIndex < state.rowsData.length - 1 &&
              state.rowsData.length > 0 && (
                <Flex
                  direction={"row"}
                  width={"113px"}
                  height={"32px"}
                  flexShrink={0}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"8px"}
                  UNSAFE_className={styles.btn_next}
                >
                  <Button
                    id="btn_next_large_detail"
                    variant="primary"
                    onPress={nextNavigation}
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                    }}
                  >
                    <>
                      <View UNSAFE_className={styles.btn_text}>
                        {t("next", { ns: "layout_components" })}
                      </View>
                      &nbsp;&nbsp;&nbsp;
                      <ArrowRight className={styles.icon_navigate} />
                    </>
                  </Button>
                </Flex>
              )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderMessageListDetailMobile = () => {
    return (
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ width: "100%", height: "100vh" }}
      >
        <View
          width={"100%"}
          top={0}
          position={"fixed"}
          height={"100vh"}
          overflow={"hidden"}
          UNSAFE_className={styles.message_list_detail_wrapper_large}
        >
          <Flex
            direction={"column"}
            position={"fixed"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            UNSAFE_className={styles.message_list_detail_parent}
          >
            <Flex
              direction={"column"}
              height={"100%"}
              alignSelf={"stretch"}
              alignItems={"center"}
              justifyContent={"start"}
            >
              <View
                position={"relative"}
                top={0}
                right={0}
                width={"100%"}
                height={"48px"}
                overflow={"hidden"}
                UNSAFE_className={styles.email_content_header_parent_large}
              >
                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={"10px"}
                  left={"10px"}
                  justifyContent={"start"}
                  gap={"18px"}
                  alignItems={"center"}
                >
                  <Flex
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"12px"}
                  >
                    <Flex
                      marginStart={"5px"}
                      marginEnd={"3px"}
                      UNSAFE_style={{
                        color: "#8f9fa6",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      <i
                        className={`bi bi-chevron-left`}
                        onClick={() => {
                          setShowMobileMenu(true);
                          actions.setShowDetail(false);
                        }}
                      >
                        <View
                          marginStart={"10px"}
                          UNSAFE_style={{ display: "inline" }}
                        >
                          Back
                        </View>
                      </i>
                    </Flex>
                    {/* <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ReplyIcon size={24} />
                  </Flex>
                  <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ReplyAllIcon size={24} />
                  </Flex>
                  <Flex direction={'row'} width={'34px'} height={'34px'} flexShrink={0} justifyContent={'center'} UNSAFE_className={styles.action_button}>
                    <ArrowForwardIcon size={24} />
                  </Flex> */}
                  </Flex>
                  {/* <View position={'relative'} width={'1px'} height={'24px'} UNSAFE_className={styles.message_list_detail_header_divider}></View>
                <Flex direction={'row'} height={'32px'} alignItems={'center'} justifyContent={'center'} width={'100px'} UNSAFE_className={styles.btn_comment}>
                  <button className={styles.btn_comment_text} style={{ background: 'transparent', border: 'none', width: '100%', fontSize: '14px' }}>
                    {t('comment', { ns: 'layout_components' })}
                  </button>
                </Flex> */}
                </Flex>

                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={"10px"}
                  right={"10px"}
                  alignItems={"center"}
                  justifyContent={"start"}
                >
                  <View
                    position={"relative"}
                    UNSAFE_className={styles.div_date}
                  >
                    {moment(state.selectedDataRow.date).format(
                      "DD.MM.YYYY HH:mm",
                    )}
                  </View>
                </Flex>
                {/* <Flex direction={'row'} position={'absolute'} top={'32px'} right={'2px'} alignItems={'center'} justifyContent={'start'}>
                <MenuTrigger>
                  <Link>
                    <span aria-label="Open context menu" role="button">
                      <i className="bi bi-three-dots fs-7"></i>
                    </span>
                  </Link>
                  <Menu width={'100%'} onAction={(key: any) => menuAction(key)}>
                    <Item key="delete" textValue="delete">
                      <i className="bi bi-trash" style={iconStyle}></i>&nbsp;&nbsp;
                      <span style={{ color: '#36474F', paddingLeft: '5px', paddingTop: '4px' }}>{t('remove', { ns: 'layout_components' })}</span>
                    </Item>
                  </Menu>
                </MenuTrigger>
              </Flex> */}
              </View>
              <Flex
                direction={"column"}
                width={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                UNSAFE_className={styles.email_content_header_parent_large}
              >
                <Flex
                  direction={"row"}
                  alignSelf={"stretch"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"18px"}
                  UNSAFE_style={{
                    borderBottom: "1px solid #eceff1",
                    paddingLeft: "13px",
                    paddingBottom: "15px",
                  }}
                >
                  <View UNSAFE_className={styles.user_img}>
                    <img
                      id="img_renderMessageListDetailMobile"
                      className={styles.avatar_desktop_light}
                      alt=""
                      src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                    />
                  </View>

                  <Flex
                    direction={"column"}
                    width={"100%"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"5px"}
                  >
                    {messageType !== "concept" && (
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        alignItems={"end"}
                        justifyContent={"space-between"}
                      >
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"start"}
                          gap={"8px"}
                        >
                          <View UNSAFE_className={styles.user_full_name}>
                            {utils.getSenderName(state.selectedDataRow.sender)}
                          </View>
                          <View
                            width={"1px"}
                            position={"relative"}
                            height={"12px"}
                            UNSAFE_className={styles.user_detail_divider}
                          ></View>
                          <View UNSAFE_className={styles.user_email}>
                            {utils.getSenderEmail(
                              state.selectedDataRow.mailFrom,
                            )}
                          </View>
                        </Flex>
                      </Flex>
                    )}
                    <Flex
                      direction={"column"}
                      alignSelf={"stretch"}
                      alignItems={"start"}
                      justifyContent={"start"}
                      gap={"8px"}
                      UNSAFE_className={styles.subject_parent}
                    >
                      <View
                        position={"relative"}
                        alignSelf={"stretch"}
                        UNSAFE_className={styles.email_subject}
                      >
                        {state.selectedDataRow.subject}
                      </View>
                      <RecipientToField
                        recipient={state.selectedDataRow?.RECIPIENT}
                      />
                      <Flex
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        gap={"6px"}
                      >
                        <View UNSAFE_className={styles.address}>
                          {state.messageContent.MESSAGE?.folderName}
                        </View>
                        {state.messageContent.MESSAGE?.projectName && (
                          <>
                            <View UNSAFE_className={styles.icon_chevron_right}>
                              {" "}
                              {">"}{" "}
                            </View>
                            <View UNSAFE_className={styles.address}>
                              {state.messageContent.MESSAGE?.projectName}
                            </View>
                          </>
                        )}
                        {state.messageContent.MESSAGE?.activityName && (
                          <>
                            <View UNSAFE_className={styles.icon_chevron_right}>
                              {" "}
                              {">"}{" "}
                            </View>
                            <View UNSAFE_className={styles.address}>
                              {state.messageContent.MESSAGE?.activityName}
                            </View>
                          </>
                        )}
                      </Flex>
                      {state.messageContent.participantsName && (
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={"6px"}
                        >
                          <View UNSAFE_className={styles.address}>
                            Participants:{" "}
                            {state.messageContent.participantsName}
                          </View>
                        </Flex>
                      )}
                      <MessageAttachment
                        store={store}
                        styles={styles}
                        messageContent={state.messageContent}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <View
                width={"100%"}
                position={"relative"}
                UNSAFE_className={styles.email_content}
                height={"55%"}
                UNSAFE_style={{ backgroundColor: "#fff", padding: "5px" }}
              >
                {/* <div
                  dangerouslySetInnerHTML={{
                    __html: messageType != 'concept' ? utils.oldEmailContentFix(state.messageContent.MESSAGE?.PREVIEW?.content) : state.messageContent.messageTemplate?.BODY?.string,
                  }}
                /> */}

                <iframe
                  srcDoc={
                    messageType !== "concept"
                      ? utils.oldEmailContentFix(
                          state.messageContent.MESSAGE?.PREVIEW?.content,
                        )
                      : state.messageContent.messageTemplate?.BODY?.string
                  }
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: "#fff",
                  }}
                  title="Email Content"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                />
              </View>
            </Flex>
          </Flex>
          <Flex
            direction={"column"}
            position={"absolute"}
            bottom={"80px"}
            left={"calc(50% - 188px)"}
            alignItems={"center"}
            justifyContent={"end"}
          >
            <Flex
              direction={"row"}
              width={"360px"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"32px"}
            >
              {state.navigationIndex > 0 && (
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"8px"}
                  UNSAFE_className={styles.btn_previous}
                >
                  <Button
                    variant="primary"
                    onPress={previousNavigation}
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                    }}
                  >
                    <>
                      <ArrowLeft className={styles.icon_navigate} />
                      &nbsp;&nbsp;&nbsp;
                      <View UNSAFE_className={styles.btn_text}>
                        {t("previous", { ns: "layout_components" })}
                      </View>
                    </>
                  </Button>
                </Flex>
              )}
              <Flex
                direction={"row"}
                height={"18px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                UNSAFE_style={{
                  fontWeight: "bold",
                  overflow: "hidden",
                  padding: "4px 0px",
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#1976d2",
                }}
              >
                <div key={state.navigationIndex}>
                  {state.totalMessageListRec > 999
                    ? `${999}+ messages`
                    : `${state.totalMessageListRec} messages`}
                </div>
              </Flex>
              {state.navigationIndex < state.rowsData.length - 1 &&
                state.rowsData.length > 0 && (
                  <Flex
                    direction={"row"}
                    width={"113px"}
                    height={"32px"}
                    flexShrink={0}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"8px"}
                    UNSAFE_className={styles.btn_next}
                  >
                    <Button
                      id="btn_next_large_mobile"
                      variant="primary"
                      onPress={nextNavigation}
                      UNSAFE_style={{
                        background: "transparent",
                        border: "none",
                        width: "100%",
                      }}
                    >
                      <>
                        <View UNSAFE_className={styles.btn_text}>
                          {t("next", { ns: "layout_components" })}
                        </View>
                        &nbsp;&nbsp;&nbsp;
                        <ArrowRight className={styles.icon_navigate} />
                      </>
                    </Button>
                  </Flex>
                )}
            </Flex>
          </Flex>
          <div className={styles.action_button_parent}>
            <div className={styles.action_button_group}>
              <div
                className={styles.action_icon_parent}
                onClick={() => handleReplyClick("REPLY")}
              >
                <div className={styles.iconReplyParent}>
                  <ReplyIcon size={24} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "5px" }}
                  >
                    Reply
                  </div>
                </div>
              </div>
              <div
                className={styles.action_icon_parent}
                onClick={() => handleReplyClick("REPLYALL")}
              >
                <div className={styles.iconReplyAllParent}>
                  <ReplyAllIcon size={24} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "5px" }}
                  >
                    Reply All
                  </div>
                </div>
              </div>
              <div
                className={styles.action_icon_parent}
                onClick={() =>
                  invokeNewMessageCreating(
                    state.selectedDataRow?.id,
                    "MessageDialog",
                    [],
                    [],
                  )
                }
              >
                <div className={styles.iconArrowForwardParent}>
                  <ArrowForwardIcon size={24} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "3px" }}
                  >
                    Forward
                  </div>
                </div>
              </div>
              <div className={styles.action_icon_parent}>
                <div className={styles.iconAiChatParent}>
                  <AIChatIconColored size={24} height={16} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "5px" }}
                  >
                    AI chat
                  </div>
                </div>
              </div>
              <div className={styles.action_icon_parent}>
                <div className={styles.iconCommentParent}>
                  <CommentIcon size={16} height={16} color="#1976D2" />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "5px" }}
                  >
                    Comment
                  </div>
                </div>
              </div>
              <div className={styles.action_icon_parent}>
                <div className={styles.iconMoreParent}>
                  <MoreIcon size={24} />
                  <div
                    className={styles.action_icon_text}
                    style={{ paddingTop: "4px" }}
                  >
                    More
                  </div>
                </div>
              </div>
            </div>
          </div>
        </View>
      </div>
    );
  };

  useEffect(() => {
    refreshMessageCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showDetail]);

  // Effect to handle mobile menu state when showing detail view
  useEffect(() => {
    if (state.showDetail && isMobile) {
      setShowMobileMenu(false);
    }
  }, [state.showDetail, isMobile, setShowMobileMenu]);

  const renderContent = () => {
    if (!state.showDetail) {
      return renderMessageList();
    } else if (isMobile) {
      return renderMessageListDetailMobile();
    } else {
      return renderMessageListDetailLarge();
    }
  };

  const closeMessageDialog = (reload: Boolean) => {
    actions.setShowNewMessageDialog(false);
    // Clear reply state when dialog is closed
    actions.setReplyType("");
    actions.setReplyMessageObj(null);
    actions.setReplyEmailAttachments([]);

    if (reload) {
      reloadMessageList();
    }
  };

  if (state.showLoader) {
    return <Loader />;
  } else {
    return (
      <>
        {state.rowsData?.length > 0 ? (
          <div ref={refs.componentRef}>
            {isMobile ||
            (refs.componentRef.current &&
              refs.componentRef.current.offsetWidth < 780) ? (
              renderContent()
            ) : (
              <View
                width={"100%"}
                position={"relative"}
                height={"889px"}
                UNSAFE_className={styles.message_list_wrapper}
              >
                <View
                  position={"relative"}
                  top={0}
                  left={0}
                  width={"35%"}
                  height={"100%"}
                  overflow={"hidden"}
                  UNSAFE_className={styles.message_list_parent}
                >
                  <Flex
                    position={"relative"}
                    top={0}
                    left={0}
                    direction={"column"}
                    alignItems={"start"}
                    justifyContent={"start"}
                  >
                    <View
                      position={"relative"}
                      top={0}
                      left={0}
                      width={"100%"}
                      height={"64px"}
                      overflow={"hidden"}
                      UNSAFE_className={styles.messages_list_header_parent}
                      UNSAFE_style={{ display: "" }}
                    >
                      <Flex
                        direction={"row"}
                        position={"absolute"}
                        top={24}
                        height={"32px"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        marginStart={12}
                        UNSAFE_className={styles.btn_new_email}
                      >
                        {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                        <Button
                          id="btn_new_email_2"
                          variant="primary"
                          UNSAFE_style={{
                            background: "transparent",
                            border: "none",
                            width: "100%",
                            fontSize: "14px",
                          }}
                          onPress={(e) => {
                            actions.setShowNewMessageDialog(true);
                            //ToastQueue.info('Under Development', { timeout: 50 });
                          }}
                        >
                          <>
                            <Plus
                              size={18}
                              className={styles.btn_new_email_text}
                            />
                            <span className={styles.btn_new_email_text}>
                              {t("new_email", { ns: "layout_components" })}
                            </span>
                          </>
                        </Button>
                      </Flex>
                      <Flex
                        position={"absolute"}
                        top={24}
                        left={188}
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        gap={15}
                        UNSAFE_style={{ display: "none" }}
                      >
                        <ArrowClockwise
                          className={styles.message_list_header_icon}
                          onClick={reloadMessageList}
                        />
                        {/* <SearchIcon size={24} /> */}
                        <SearchButton />
                        {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                        <FilterButton />
                        <MoreIcon size={24} />
                      </Flex>
                    </View>
                    <Flex maxHeight="100vh" width={"100%"}>
                      <ListBox
                        autoFocus
                        id="listMessageCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        defaultSelectedKeys={state.selectedKeys}
                        selectedKeys={state.selectedKeys}
                        aria-label="Message List Component"
                        items={listMessageList.items}
                        isLoading={listMessageList.isLoading}
                        onLoadMore={
                          state.totalMessageListRec > state.rowsData.length
                            ? listMessageList.loadMore
                            : undefined
                        }
                        width={"100%"}
                        UNSAFE_style={{ padding: "0 0 0 5px" }}
                      >
                        {(item) => (
                          <Item key={item.id}>
                            {messageType !== "concept" ? (
                              <CardComponent item={item} />
                            ) : (
                              <ConceptCardComponent item={item} />
                            )}
                          </Item>
                        )}
                      </ListBox>
                    </Flex>
                  </Flex>
                </View>
                <View
                  position={"absolute"}
                  top={0}
                  left={"35%"}
                  width={"1px"}
                  height={"100%"}
                  UNSAFE_className={styles.message_list_divider}
                ></View>
                {state.showLoaderDetail || listMessageList.isLoading ? (
                  <View
                    position={"absolute"}
                    top={15}
                    left={"calc(35% + 1px)"}
                    width={"calc(65% - 1px)"}
                    height={"98%"}
                    overflow={"hidden"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                  >
                    <Loader />
                  </View>
                ) : (
                  <View
                    position={"absolute"}
                    top={15}
                    left={"calc(35% + 1px)"}
                    width={"calc(65% - 1px)"}
                    height={"98%"}
                    overflow={"hidden"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                  >
                    <Flex
                      direction={"column"}
                      position={"absolute"}
                      top={0}
                      left={0}
                      width={"100%"}
                      height={"98%"}
                      alignItems={"start"}
                      justifyContent={"start"}
                      UNSAFE_className={styles.message_list_detail_parent}
                    >
                      <Flex
                        direction={"column"}
                        height={"98%"}
                        alignSelf={"stretch"}
                        alignItems={"center"}
                        justifyContent={"start"}
                      >
                        <View
                          position={"relative"}
                          top={0}
                          right={0}
                          width={"100%"}
                          height={"64px"}
                          overflow={"hidden"}
                          UNSAFE_className={
                            styles.message_list_detail_header_parent
                          }
                        >
                          <Flex
                            direction={"row"}
                            position={"absolute"}
                            top={"10px"}
                            left={"10px"}
                            justifyContent={"start"}
                            gap={"18px"}
                            alignItems={"center"}
                          >
                            <Flex
                              direction={"row"}
                              alignItems={"center"}
                              justifyContent={"center"}
                              gap={"12px"}
                            >
                              <View>
                                {isMobile && (
                                  <ArrowLeft
                                    size={24}
                                    className={styles.message_list_header_icon}
                                  />
                                )}
                              </View>
                              <View
                                width={"34px"}
                                height={"34px"}
                                UNSAFE_className={styles.action_button}
                              >
                                <ActionButton
                                  UNSAFE_style={{
                                    background: "transparent",
                                    border: "none",
                                  }}
                                  onPress={() => handleReplyClick("REPLY")}
                                  aria-label="Reply"
                                >
                                  <ReplyIcon size={24} />
                                </ActionButton>
                              </View>
                              <View
                                width={"34px"}
                                height={"34px"}
                                UNSAFE_className={styles.action_button}
                              >
                                <ActionButton
                                  UNSAFE_style={{
                                    background: "transparent",
                                    border: "none",
                                  }}
                                  onPress={() => handleReplyClick("REPLYALL")}
                                  aria-label="Reply All"
                                >
                                  <ReplyAllIcon size={24} />
                                </ActionButton>
                              </View>
                              <View
                                width={"34px"}
                                height={"34px"}
                                UNSAFE_className={styles.action_button}
                              >
                                <ActionButton
                                  UNSAFE_style={{
                                    background: "transparent",
                                    border: "none",
                                  }}
                                  onPress={() =>
                                    invokeNewMessageCreating(
                                      state.selectedDataRow?.id,
                                      "MessageDialog",
                                      [],
                                      [],
                                    )
                                  }
                                  aria-label="Forward/New Message"
                                >
                                  <ArrowForwardIcon size={24} />
                                </ActionButton>
                              </View>
                            </Flex>
                            <View
                              position={"relative"}
                              width={"1px"}
                              height={"24px"}
                              UNSAFE_className={
                                styles.message_list_detail_header_divider
                              }
                            ></View>
                            <Flex
                              direction={"row"}
                              height={"32px"}
                              alignItems={"center"}
                              justifyContent={"center"}
                              width={"100px"}
                              UNSAFE_className={styles.btn_comment}
                            >
                              <button
                                className={styles.btn_comment_text}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  width: "100%",
                                  fontSize: "14px",
                                }}
                              >
                                {t("comment", { ns: "layout_components" })}
                              </button>
                            </Flex>
                          </Flex>
                          <Flex
                            direction={"row"}
                            position={"relative"}
                            UNSAFE_style={{ float: "right", padding: "10px" }}
                            alignItems={"center"}
                            justifyContent={"start"}
                            gap={"10px"}
                          >
                            <RemoveMessageButton />
                            <MoreIcon size={24} />
                          </Flex>
                        </View>
                        <Flex
                          direction={"column"}
                          width={"100%"}
                          height={"auto"}
                          alignItems={"start"}
                          justifyContent={"start"}
                          UNSAFE_className={styles.email_content_header_parent}
                          UNSAFE_style={{ borderBottom: "1px solid #eceff1" }}
                        >
                          <Flex
                            direction={"row"}
                            alignSelf={"stretch"}
                            alignItems={"start"}
                            justifyContent={"start"}
                            gap={"18px"}
                          >
                            <View UNSAFE_className={styles.user_img}>
                              <img
                                id="img_MessageList"
                                className={styles.avatar_desktop_light}
                                alt=""
                                src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                              />
                            </View>
                            <Flex
                              direction={"column"}
                              width={"100%"}
                              alignItems={"start"}
                              justifyContent={"start"}
                              gap={"5px"}
                            >
                              <Flex
                                direction={"row"}
                                alignSelf={"stretch"}
                                alignItems={"end"}
                                justifyContent={"space-between"}
                              >
                                {messageType !== "concept" && (
                                  <Flex
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"start"}
                                    gap={"8px"}
                                  >
                                    <View
                                      UNSAFE_className={styles.user_full_name}
                                    >
                                      {utils.getSenderName(
                                        state.selectedDataRow.sender,
                                      )}
                                    </View>
                                    <View
                                      width={"1px"}
                                      position={"relative"}
                                      height={"12px"}
                                      UNSAFE_className={
                                        styles.user_detail_divider
                                      }
                                    ></View>
                                    <View UNSAFE_className={styles.user_email}>
                                      {utils.getSenderEmail(
                                        state.selectedDataRow.mailFrom,
                                      )}
                                    </View>
                                  </Flex>
                                )}
                                <View
                                  position={"relative"}
                                  UNSAFE_className={styles.div_date}
                                >
                                  {moment(state.selectedDataRow.date).format(
                                    "DD.MM.YYYY HH:mm",
                                  )}
                                </View>
                              </Flex>
                              <Flex
                                direction={"column"}
                                alignSelf={"stretch"}
                                alignItems={"start"}
                                justifyContent={"start"}
                                gap={"8px"}
                                UNSAFE_className={styles.subject_parent}
                              >
                                <View
                                  position={"relative"}
                                  alignSelf={"stretch"}
                                  UNSAFE_className={styles.email_subject}
                                >
                                  {state.selectedDataRow.subject}
                                </View>
                                <RecipientToField
                                  recipient={state.selectedDataRow?.RECIPIENT}
                                />
                                <Flex
                                  direction={"row"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  gap={"6px"}
                                >
                                  <View UNSAFE_className={styles.address}>
                                    {state.messageContent.MESSAGE?.folderName}
                                  </View>
                                  {state.messageContent.MESSAGE
                                    ?.projectName && (
                                    <>
                                      <View
                                        UNSAFE_className={
                                          styles.icon_chevron_right
                                        }
                                      >
                                        {">"}
                                      </View>
                                      <View UNSAFE_className={styles.address}>
                                        {
                                          state.messageContent.MESSAGE
                                            ?.projectName
                                        }
                                      </View>
                                    </>
                                  )}
                                  {state.messageContent.MESSAGE
                                    ?.activityName && (
                                    <>
                                      <View
                                        UNSAFE_className={
                                          styles.icon_chevron_right
                                        }
                                      >
                                        {">"}
                                      </View>
                                      <View UNSAFE_className={styles.address}>
                                        {
                                          state.messageContent.MESSAGE
                                            ?.activityName
                                        }
                                      </View>
                                    </>
                                  )}
                                </Flex>
                                {state.messageContent.participantsName && (
                                  <Flex
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                    gap={"6px"}
                                  >
                                    <View UNSAFE_className={styles.address}>
                                      Participants:&nbsp;
                                      {state.messageContent.participantsName}
                                    </View>
                                  </Flex>
                                )}
                                <MessageAttachment
                                  store={store}
                                  styles={styles}
                                  messageContent={state.messageContent}
                                />
                              </Flex>
                            </Flex>
                          </Flex>
                        </Flex>
                        <View
                          width={"93%"}
                          position={"relative"}
                          top={"16px"}
                          UNSAFE_className={styles.email_content}
                          flex={1}
                          minHeight={0}
                          UNSAFE_style={{ overflow: "hidden" }}
                        >
                          {/* <div
                            title={state.messageContent.MESSAGE?.PREVIEW?.content}
                            dangerouslySetInnerHTML={{
                              __html: messageType != 'concept' ? utils.oldEmailContentFix(state.messageContent.MESSAGE?.PREVIEW?.content) : state.messageContent.messageTemplate?.BODY?.string,
                            }}
                          /> */}
                          <iframe
                            srcDoc={
                              messageType !== "concept"
                                ? utils.oldEmailContentFix(
                                    state.messageContent.MESSAGE?.PREVIEW
                                      ?.content,
                                  )
                                : state.messageContent.messageTemplate?.BODY
                                    ?.string
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              backgroundColor: "#fff",
                            }}
                            title="Email Content"
                            sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                          />
                        </View>
                        {/* Navigation Buttons - part of flex flow */}
                        <Flex
                          direction={"row"}
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          marginTop={"size-200"}
                          marginBottom={"size-200"}
                          flexShrink={0}
                        >
                          <Flex
                            direction={"row"}
                            width={"258px"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            gap={"32px"}
                          >
                            {state.navigationIndex > 0 && (
                              <Flex
                                direction={"row"}
                                height={"32px"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                gap={"8px"}
                                UNSAFE_className={styles.btn_previous}
                              >
                                <Button
                                  variant="primary"
                                  onPress={previousNavigation}
                                  UNSAFE_style={{
                                    background: "transparent",
                                    border: "none",
                                    width: "100%",
                                  }}
                                >
                                  <>
                                    <ArrowLeft
                                      className={styles.icon_navigate}
                                    />
                                    &nbsp;&nbsp;&nbsp;
                                    <View UNSAFE_className={styles.btn_text}>
                                      {t("previous", {
                                        ns: "layout_components",
                                      })}
                                    </View>
                                  </>
                                </Button>
                              </Flex>
                            )}
                            {state.navigationIndex <=
                              state.rowsData.length - 1 &&
                              state.rowsData.length > 0 && (
                                <Flex
                                  direction={"row"}
                                  width={"113px"}
                                  height={"32px"}
                                  flexShrink={0}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  gap={"8px"}
                                  UNSAFE_className={styles.btn_next}
                                >
                                  <Button
                                    id="btn_next_message_detail"
                                    variant="primary"
                                    onPress={nextNavigation}
                                    UNSAFE_style={{
                                      background: "transparent",
                                      border: "none",
                                      width: "100%",
                                    }}
                                  >
                                    <>
                                      <View UNSAFE_className={styles.btn_text}>
                                        {t("next", { ns: "layout_components" })}
                                      </View>
                                      &nbsp;&nbsp;&nbsp;
                                      <ArrowRight
                                        className={styles.icon_navigate}
                                      />
                                    </>
                                  </Button>
                                </Flex>
                              )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>
                  </View>
                )}
              </View>
            )}
          </div>
        ) : (
          <View
            position={"relative"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            overflow={"hidden"}
            UNSAFE_className={styles.message_list_parent}
          >
            <Flex
              position={"relative"}
              top={0}
              left={0}
              direction={"column"}
              alignItems={"start"}
              justifyContent={"start"}
            >
              <View
                position={"relative"}
                top={0}
                left={0}
                width={"100%"}
                height={"64px"}
                overflow={"hidden"}
                UNSAFE_className={styles.messages_list_header_parent}
                UNSAFE_style={{ display: "" }}
              >
                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={24}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  marginStart={12}
                  UNSAFE_className={styles.btn_new_email}
                >
                  {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                  <Button
                    id="btn_new_email_3"
                    variant="primary"
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                    onPress={() => {
                      //setShowNewMessageDialog(true);
                    }}
                  >
                    <>
                      <Plus size={18} className={styles.btn_new_email_text} />
                      <span className={styles.btn_new_email_text}>
                        {t("new_email", { ns: "layout_components" })}
                      </span>
                    </>
                  </Button>
                </Flex>
                <Flex
                  position={"absolute"}
                  top={25}
                  right={15}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={18}
                  UNSAFE_style={{ display: "none" }}
                >
                  <ArrowClockwise
                    className={styles.message_list_header_icon}
                    onClick={reloadMessageList}
                  />
                  {/* <SearchIcon size={24} /> */}
                  <SearchButton />
                  {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                  <FilterButton />
                  <MoreIcon size={24} />
                </Flex>
              </View>
            </Flex>
            <NoResultsMessage />
          </View>
        )}
        <NewMessageComponent
          key={state.showNewMessageDialog ? "dialog-open" : "dialog-closed"}
          isOpen={state.showNewMessageDialog}
          windowIndex={0}
          selectedItemId={state.messageNodeId}
          replyType={state.replyType}
          messageObj={state.replyMessageObj}
          emailAttachments={state.replyEmailAttachments}
          messageType={messageType}
          closeMessageDialog={closeMessageDialog}
        />
      </>
    );
  }
};

export const MessagesComponent = LayoutComponent(MessagesList);
